import { FormInstance } from "antd";
import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { ActionLogInjection } from "features/actionLogManagement/di/ActionLogInjection";
import { ActionLogDetailEntity } from "features/actionLogManagement/domain/entities/ActionLogDetailEntity";
import { ActionLogRequestEntity } from "features/actionLogManagement/domain/entities/ActionLogRequestEntity";
import { ActionLogUseCase } from "features/actionLogManagement/domain/useCases/ActionLogUseCase";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { IActionLogObservedState } from "../interfaces/IActionLogManagement";

class ActionLogInput implements Disposable {
  public fetchList = new BehaviorSubject<ActionLogRequestEntity>({});
  public fetchDetail = new BehaviorSubject<ActionLogRequestEntity>({});
  public fetchUserList = new BehaviorSubject<BaseUserRequestModel>({});

  [Symbol.dispose](): void {
    this.fetchDetail.complete();
    this.fetchList.complete();
    this.fetchUserList.complete();
  }
}

class ActionLogOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onDetailSuccess.complete();
  }
}

@injectable()
export class ActionLogViewModel extends BasedObservedViewModel<
  ActionLogInput,
  ActionLogOutput,
  IActionLogObservedState
> {
  private actionLogUseCase: ActionLogUseCase;

  constructor() {
    super(new ActionLogInput(), new ActionLogOutput());
    this.actionLogUseCase = ActionLogInjection.getUseCase();
  }

  protected override createObservedState(): IActionLogObservedState {
    return {
      list: [],
      detail: new ActionLogDetailEntity(),
      loading: false,
      paging: {},
      userList: [],
    };
  }

  public buildTableQuery(
    pagination: any,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: ActionLogRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params, guard) => {
      try {
        guard?.runIfLatest(() => this.setObservedState({ loading: true }));
        this.setObservedState({ list: [] });
        const res = await this.actionLogUseCase.getList(params);
        if (res?.data) {
          this.setObservedState({
            list: res?.data,
            paging: res?.pagination,
          });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
        this.setObservedState({
          list: [],
          paging: {},
        });
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.fetchUserList, async (params) => {
      try {
        const res = await this.actionLogUseCase.getUserList(params);
        if (res) {
          this.setObservedState({ userList: res });
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        const res = await this.actionLogUseCase.getDetail(params);
        if (res?.data) {
          this.setObservedState({ detail: res?.data });
          this.output.onDetailSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });
  }
}
